import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import { DialogTitle, LanguageString } from '../Common';
import * as XLSX from 'xlsx';
import { useStudentsByCaseIds } from '../../hooks';

const useStyles = makeStyles(theme => ({
  dropZone: {
    border: '2px dashed',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover
    },
    '&.dragover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light
    }
  },
  uploadIcon: {
    fontSize: '3rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1)
  },
  uploadInput: {
    display: 'none'
  },
  columnHeaderLabel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  extractedInfo: {
    marginTop: theme.spacing(2)
  }
}));

interface NotificationBlastFileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  columnHeader: string;
  caseIdMatchPattern?: string;
  onStudentsSelected?: (students: any[]) => void;
}

export const NotificationBlastFileUploadDialog: React.FC<NotificationBlastFileUploadDialogProps> = ({
  open,
  onClose,
  columnHeader,
  caseIdMatchPattern,
  onStudentsSelected
}) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedCount, setExtractedCount] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [getStudentsByCaseIds] = useStudentsByCaseIds();

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    if (!file) return;

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setError('Please select a valid .csv or .xlsx file');
      return;
    }

    void processFile(file);
  };

  const processFile = async (file: File) => {
    setLoading(true);
    setError(null);
    setExtractedCount(null);

    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result;
          if (typeof result === 'string') {
            resolve(result);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsBinaryString(file);
      });

      const workbook = XLSX.read(data, { type: 'binary' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      if (!sheet) {
        setError('The file appears to be empty');
        return;
      }

      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rows.length === 0) {
        setError('No data found in the file');
        return;
      }

      // Column resolution: exact match → first column fallback
      const keys = Object.keys(rows[0] as any);
      const resolvedKey = keys.includes(columnHeader) ? columnHeader : keys[0];

      if (!resolvedKey) {
        setError('The file does not contain any columns');
        return;
      }

      const caseIds = rows
        .map((row: any) => String(row[resolvedKey] || '').trim())
        .filter((v: string) => v.length > 0);

      if (caseIds.length === 0) {
        setError('No case IDs found in the expected column');
        return;
      }

      // If the site has a match regex on the caseIDNum property type, validate all IDs
      if (caseIdMatchPattern) {
        try {
          const pattern = new RegExp(caseIdMatchPattern);
          const invalid = caseIds.filter((id: string) => !pattern.test(id));
          if (invalid.length > 0) {
            const firstBad = invalid[0];
            setError(`Found ${invalid.length} case ID(s) with invalid format. First invalid: "${firstBad}"`);
            return;
          }
        } catch (e) {
          console.error('Invalid regex pattern:', caseIdMatchPattern);
        }
      }

      // Query the server for matching students
      const result = await getStudentsByCaseIds({
        variables: { searchParams: { caseIds } }
      });

      const students = result.data?.students?.students || [];

      if (students.length === 0) {
        setError('No matching students found for the provided case IDs');
        return;
      }

      // Call the callback with the selected students
      if (onStudentsSelected) {
        onStudentsSelected(students);
      }

      setExtractedCount(students.length);

      // Close dialog after a brief delay so the user can see the success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error processing file:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} aria-labelledby="Upload Recipients">
      <DialogTitle onClose={onClose}>
        <LanguageString groupName="NOTIFICATION_BLAST" resourceName="UPLOAD_TITLE" defaultText="Upload Recipient List" />
      </DialogTitle>
      <DialogContent dividers>
        <Box className={`${styles.dropZone} ${dragOver ? 'dragover' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
        >
          <CloudUploadIcon className={styles.uploadIcon} />
          <Typography variant="h6">
            <LanguageString groupName="NOTIFICATION_BLAST" resourceName="DRAG_DROP_TEXT"
              defaultText="Drag and drop your CSV or XLSX file here" />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <LanguageString groupName="NOTIFICATION_BLAST" resourceName="OR_CLICK_TEXT"
              defaultText="or click to select a file" />
          </Typography>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleInputChange}
          className={styles.uploadInput}
        />

        <Typography variant="subtitle2" className={styles.columnHeaderLabel}>
          <LanguageString groupName="NOTIFICATION_BLAST" resourceName="EXPECTED_COLUMN"
            defaultText="Expected column header:" />
        </Typography>
        <Typography variant="body2" color="primary">
          <strong>{columnHeader}</strong>
        </Typography>

        {error && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={60} mt={2}>
            <CircularProgress />
          </Box>
        )}

        {extractedCount !== null && !loading && !error && (
          <Card variant="outlined" className={styles.extractedInfo}>
            <CardContent>
              <Alert severity="success">
                Successfully loaded {extractedCount} student(s)
              </Alert>
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          <LanguageString groupName="GENERAL" resourceName="CLOSE" defaultText="Close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationBlastFileUploadDialog;
